// import node module libraries
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { FormSelect } from 'components/elements/form-select/FormSelect';
import { Link } from 'react-router-dom';
import { Col, Form, Image, Row, Table } from 'react-bootstrap';
import {
	useFilters,
	useGlobalFilter,
	usePagination,
	useTable
} from 'react-table';

// import custom components
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import utility file
import { Avatar } from '../../../elements/bootstrap/Avatar';
import EleveService from '../../services/EleveService';
import SalledeclasseService from '../../services/SalledeclasseService';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const StudentsListItems = () => {
	const [originalStudents, setOriginalStudents] = useState([]);
	const [students, setStudents] = useState([]);
	const [salledeclasse, setSalledeclasse] = useState([]);
	const [classeOptions, setClasseOptions] = useState([]);

	useEffect(() => {
		const fetchClassOptions = async () => {
			try {
				const classes = await SalledeclasseService.getClassOptions();
				setClasseOptions(classes);
			} catch (error) {
				console.error('Error fetching class options', error);
			}
		};

		fetchClassOptions();
	}, []);

	const CLASSE_CHOICES = classeOptions;

	useEffect(() => {
		Promise.all([
			EleveService.getAllEleves(),
			SalledeclasseService.getAllSalledeclasse()
		]).then(([elevesData, salledeclasseData]) => {
			setOriginalStudents(elevesData);
			setStudents(elevesData);
			setSalledeclasse(salledeclasseData);
		});
	}, []);

	React.forwardRef(({ children, onClick }, ref) => (
		<Link
			to=""
			ref={ref}
			onClick={(e) => {
				e.preventDefault();
				onClick(e);
			}}
			className="btn-icon btn btn-ghost btn-sm rounded-circle"
		>
			{children}
		</Link>
	));

	const formatIntituleClasse = (value) => {
		return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
	};

	const formatValue = (value, columnId) => {
		if (typeof value === 'string') {
			if (columnId === 'nom' && value) {
				return value.toUpperCase();
			} else if (
				columnId === 'genre' ||
				(columnId === 'statut_eleve' && value)
			) {
				return (
					value && value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
				);
			} else if (
				columnId === 'lieu_naissance' ||
				(columnId === 'prenom' && value)
			) {
				return value.replace(/[^-'\s]+/g, function (word) {
					return word.replace(/^./, function (first) {
						return first.toUpperCase();
					});
				});
			} else {
				return value;
			}
		} else if (columnId === 'salledeclasse') {
			return value && value.intitule_classe
				? formatIntituleClasse(value.intitule_classe).toUpperCase()
				: ''; // Return the intitule_classe property
		}
		return value;
	};

	const getInitials = (student) => {
		const firstName = student.prenom
			? student.prenom.charAt(0).toUpperCase()
			: '';
		const lastName = student.nom ? student.nom.charAt(0).toUpperCase() : '';
		return `${lastName} ${firstName}`;
	};

	const formatStatutEleve = (value) => {
		// Remplacez les underscores par des espaces et mettez la première lettre en majuscule
		return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
	};

	const getSalledeclasseById = (salledeclasseId) => {
		return salledeclasse.find((item) => item.id === salledeclasseId) ?? {};
	};

	const columns = useMemo(
		() => [
			{ accessor: 'id', Header: 'ID', show: false },
			{
				accessor: 'nom',
				Header: 'Nom',
				Cell: ({ value, row }) => {
					const initials = getInitials(row.original);
					const imageUrl = `${backendUrl}${row.original.photo}`;
					return (
						<div className="d-flex align-items-center">
							{row.original.photo ? (
								<Image
									src={imageUrl}
									alt=""
									className="rounded-circle avatar-md"
								/>
							) : (
								<Avatar
									type="initial"
									size="md"
									name={initials}
									className="rounded-circle"
									alt={initials}
								/>
							)}
							{value ? (
								<Link to={`/education/eleves/${row.original.id}`}>
									<h5 className="mb-0 ms-2">{formatValue(value, 'nom')}</h5>
								</Link>
							) : null}
						</div>
					);
				}
			},

			{
				accessor: 'prenom',
				Header: 'Prenom',
				Cell: ({ value }) => <div>{formatValue(value, 'prenom')}</div>
			},
			{ accessor: 'date_naissance', Header: 'Date de naissance' },
			{
				accessor: 'lieu_naissance',
				Header: 'Lieu de naissance',
				Cell: ({ value }) => <div>{formatValue(value, 'lieu_naissance')}</div>
			},
			{
				accessor: 'genre',
				Header: 'Genre',
				Cell: ({ value }) => <div>{formatValue(value, 'genre')}</div>
			},
			{
				accessor: 'statut_eleve',
				Header: "Statut de l'élève",
				Cell: ({ value }) => <div>{formatStatutEleve(value)}</div>
			},
			{
				accessor: 'salledeclasse',
				Header: 'Classe',
				Cell: ({ value }) => <div>{formatValue(value, 'salledeclasse')}</div>
			}
		],
		[]
	);

	const data = useMemo(() => {
		return students.map((student) => ({
			...student,
			salledeclasse: getSalledeclasseById(student.salledeclasse)
		}));
	}, [students]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		nextPage,
		previousPage,
		state,
		gotoPage,
		pageCount,
		prepareRow,
		setGlobalFilter
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
				hiddenColumns: columns.map((column) => {
					if (column.show === false) return column.accessor || column.id;
					else return false;
				})
			}
		},
		useFilters,
		useGlobalFilter,
		usePagination
	);

	const { pageIndex, globalFilter } = state;

	const getFilterTerm = (event) => {
		let filterTerm = event.target.value;
		if (filterTerm !== '') {
			const newStudentsList = originalStudents.filter((student) => {
				return student.salledeclasse === Number(filterTerm);
			});
			setStudents(newStudentsList);
		} else {
			setStudents(originalStudents);
		}
	};

	return (
		<Fragment>
			<div className="overflow-hidden p-3">
				<Row className="justify-content-md-between mb-4 mb-xl-0 ">
					<Col xxl={3} lg={4} md={6} xs={12}>
						<GlobalFilter
							filter={globalFilter}
							setFilter={setGlobalFilter}
							placeholder="Rechercher un élève"
						/>
					</Col>
					<Col xxl={3} lg={4} md={6} xs={12}>
						{/* records filtering options */}
						<Form.Control
							as={FormSelect}
							placeholder="Filtrer par salle de classe"
							options={CLASSE_CHOICES}
							onChange={getFilterTerm}
						/>
					</Col>
				</Row>
			</div>

			<Table
				hover
				responsive
				{...getTableProps()}
				className="table text-nowrap table-centered"
			>
				<thead className="table-light">
					{headerGroups.map((headerGroup) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<th {...column.getHeaderProps()}>{column.render('Header')}</th>
							))}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{page.map((row) => {
						prepareRow(row);
						return (
							<tr {...row.getRowProps()}>
								{row.cells.map((cell) => {
									return (
										<td {...cell.getCellProps()}>{cell.render('Cell')}</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</Table>

			<Pagination
				previousPage={previousPage}
				pageCount={pageCount}
				pageIndex={pageIndex}
				gotoPage={gotoPage}
				nextPage={nextPage}
			/>
		</Fragment>
	);
};

export default StudentsListItems;
